import { useState, useEffect } from 'react';
import API_CONFIG from '../../config/api';
import './EditAdminModal.css';

function EditAdminModal({ isOpen, onClose, onSuccess, admin, adminData, onImpersonate }) {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'admin',
        isActive: true
    });
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [allPermissions, setAllPermissions] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const permissionCategories = {
        'User Verification': [
            { key: 'kyc.manage', label: 'KYC Management', icon: '🆔' },
            { key: 'users.verify.kyc', label: 'Verify KYC Documents', icon: '📄' },
            { key: 'users.view_kyc', label: 'View KYC Data', icon: '🔍' }
        ],
        'Loan Operations': [
            { key: 'loans.view', label: 'View Loans', icon: '💼' },
            { key: 'loans.approve', label: 'Approve Loans', icon: '⭐' },
            { key: 'loans.edit', label: 'Edit Loan Terms', icon: '✏️' },
            { key: 'loans.disburse', label: 'Disburse Funds', icon: '📤' }
        ],
        'User Management': [
            { key: 'users.view_all', label: 'View All Users', icon: '👥' },
            { key: 'users.edit', label: 'Edit Users', icon: '✏️' },
            { key: 'users.delete', label: 'Delete Users', icon: '🗑️' },
            { key: 'users.create', label: 'Create Users', icon: '➕' }
        ],
        'Sales Operations': [
            { key: 'leads.view', label: 'View All Leads', icon: '👁️' },
            { key: 'leads.create', label: 'Create Leads', icon: '📝' },
            { key: 'leads.edit', label: 'Edit Leads', icon: '✏️' },
            { key: 'leads.assign', label: 'Assign Leads', icon: '👤' },
            { key: 'leads.status', label: 'Update Lead Status', icon: '🔄' }
        ],
        'Support Operations': [
            { key: 'tickets.manage', label: 'Manage Tickets', icon: '🎫' },
            { key: 'support.view', label: 'View Tickets', icon: '👁️' },
            { key: 'support.assign', label: 'Assign Tickets', icon: '👥' },
            { key: 'support.resolve', label: 'Resolve Tickets', icon: '✅' }
        ],
        'Collections': [
            { key: 'loans.view', label: 'View Loans', icon: '📂' },
            { key: 'loans.edit', label: 'Edit Loans', icon: '✏️' },
            { key: 'loans.approve', label: 'Approve/Reject Loans', icon: '🛂' },
            { key: 'loans.disburse', label: 'Disburse Funds', icon: '💸' },
            { key: 'reports.view', label: 'View Reports', icon: '📑' }
        ],
        'System Management': [
            { key: 'admins.view', label: 'View Admins', icon: '🛡️' },
            { key: 'admins.manage', label: 'Manage Admins', icon: '⚙️' },
            { key: 'settings.manage', label: 'System Settings', icon: '🛠️' },
            { key: 'database.manage', label: 'Database Ops', icon: '💾' },
            { key: 'security.manage', label: 'Security Policies', icon: '🔐' },
            { key: 'permissions.manage', label: 'Permission Control', icon: '🔑' },
            { key: 'api.manage', label: 'API Management', icon: '🔌' },
            { key: 'notifications.manage', label: 'Notifications', icon: '🔔' },
            { key: 'payments.manage', label: 'Payment Gateways', icon: '💳' },
            { key: 'admin.impersonate', label: 'Login as User', icon: '🎭' },
            { key: 'users.temp_password', label: 'Temp Passwords', icon: '⏳' },
            { key: 'users.agreement_upload', label: 'Upload Agreements', icon: '📄' },
            { key: 'system.health', label: 'System Health', icon: '🏥' }
        ],
        'Reporting': [
            { key: 'analytics.view', label: 'View Analytics', icon: '📊' },
            { key: 'reports.view', label: 'View Reports', icon: '📋' },
            { key: 'logs.view', label: 'Audit Logs', icon: '📜' },
            { key: 'activity.view', label: 'Activity Monitor', icon: '📡' }
        ]
    };

    const permissionPresets = {
        sales: ['leads.view', 'leads.create', 'leads.edit', 'leads.assign', 'leads.status', 'users.view_all', 'reports.view'],
        support: ['tickets.manage', 'support.view', 'support.assign', 'support.resolve', 'users.view_all', 'users.view_kyc'],
        collection: ['loans.view', 'loans.edit', 'loans.approve', 'loans.disburse', 'reports.view'],
        branch: ['users.view_all', 'loans.view', 'kyc.manage', 'reports.view', 'activity.view'],
        accounts: ['payments.manage', 'reports.view', 'analytics.view'],
        admin: ['users.view_all', 'users.edit', 'users.create', 'loans.view', 'leads.view', 'reports.view', 'analytics.view', 'users.temp_password', 'users.agreement_upload']
    };

    useEffect(() => {
        if (admin) {
            const names = admin.name.split(' ');
            setFormData({
                firstName: names[0] || '',
                lastName: names.slice(1).join(' ') || '',
                email: admin.email,
                phone: admin.phone || '',
                role: admin.role,
                isActive: admin.status === 'Active'
            });
            // Fetch detailed admin info to get individual permissions if role isn't super_admin
            fetchDetailedAdmin(admin.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [admin]);

    const fetchDetailedAdmin = async (id) => {
        const effectiveAdminId = adminData?.userId || JSON.parse(localStorage.getItem('adminSession'))?.userId;
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/admins/${id}`, {
                headers: { 'x-user-id': effectiveAdminId }
            });
            const data = await res.json();
            if (data.success) {
                setSelectedPermissions(data.data.permissions.map(p => p.permission_name));
            }
        } catch (err) {
            console.error('Error fetching admin details:', err);
        }
    };

    const handlePermissionToggle = (permKey) => {
        if (formData.role === 'super_admin') return;

        setSelectedPermissions(prev =>
            prev.includes(permKey)
                ? prev.filter(p => p !== permKey)
                : [...prev, permKey]
        );
    };

    const applyPreset = (presetKey) => {
        if (formData.role === 'super_admin') return;
        const presetPerms = permissionPresets[presetKey] || [];
        setSelectedPermissions(presetPerms);
    };

    const selectAll = () => {
        const allKeys = Object.values(permissionCategories).flat().map(p => p.key);
        setSelectedPermissions(allKeys);
    };

    const clearAll = () => setSelectedPermissions([]);

    const toggleCategory = (category) => {
        if (formData.role === 'super_admin') return;
        const keys = permissionCategories[category].map(p => p.key);
        const hasAll = keys.every(k => selectedPermissions.includes(k));
        if (hasAll) {
            setSelectedPermissions(prev => prev.filter(p => !keys.includes(p)));
        } else {
            setSelectedPermissions(prev => Array.from(new Set([...prev, ...keys])));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        const effectiveAdminId = adminData?.userId || JSON.parse(localStorage.getItem('adminSession'))?.userId;
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/admins/${admin.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': effectiveAdminId
                },
                body: JSON.stringify({
                    ...formData,
                    permissions: selectedPermissions
                })
            });

            const data = await res.json();
            if (data.success) {
                onSuccess();
                onClose();
            } else {
                setError(data.message || 'Failed to update admin');
            }
        } catch (err) {
            setError('Server connection failed');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content edit-admin-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>⚙️ Edit Admin: {admin?.name}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {error && <div className="modal-error">{error}</div>}

                <form onSubmit={handleSubmit} className="edit-form">
                    <div className="form-section">
                        <h3>👤 Basic Details</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name</label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Last Name</label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="text"
                                    value={formData.phone}
                                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Role</label>
                            <select
                                value={formData.role}
                                onChange={e => setFormData({ ...formData, role: e.target.value })}
                            >
                                <option value="admin">Standard Admin</option>
                                <option value="sales">Sales User</option>
                                <option value="support">Support User</option>
                                <option value="collection">Collections</option>
                                <option value="branch">Branch Ops</option>
                                <option value="accounts">Accounts</option>
                                <option value="super_admin">Super Admin (All Access)</option>
                            </select>
                        </div>
                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={formData.isActive}
                                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                                Active Account
                            </label>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>🔑 Feature Access Permissions</h3>
                        <p className="section-hint">
                            Toggle specific features this admin can access. Selected: {selectedPermissions.length}
                        </p>

                        {formData.role !== 'super_admin' && (
                            <div className="permission-quick-actions">
                                <button type="button" className="pill-btn" onClick={selectAll}>Select All</button>
                                <button type="button" className="pill-btn" onClick={clearAll}>Clear All</button>
                                <div className="pill-btn-group">
                                    <span className="pill-label">Presets:</span>
                                    <button type="button" className="pill-btn" onClick={() => applyPreset('admin')}>Admin</button>
                                    <button type="button" className="pill-btn" onClick={() => applyPreset('sales')}>Sales</button>
                                    <button type="button" className="pill-btn" onClick={() => applyPreset('support')}>Support</button>
                                    <button type="button" className="pill-btn" onClick={() => applyPreset('collection')}>Collections</button>
                                    <button type="button" className="pill-btn" onClick={() => applyPreset('branch')}>Branch</button>
                                    <button type="button" className="pill-btn" onClick={() => applyPreset('accounts')}>Accounts</button>
                                </div>
                            </div>
                        )}

                        {formData.role === 'super_admin' ? (
                            <div className="all-access-alert">
                                👑 <strong>Super Admin</strong> has all permissions enabled by default.
                            </div>
                        ) : (
                            Object.entries(permissionCategories).map(([category, perms]) => (
                                <div key={category} className="permission-category-group">
                                    <div className="category-head">
                                        <h4>{category}</h4>
                                        <button
                                            type="button"
                                            className="pill-btn ghost"
                                            onClick={() => toggleCategory(category)}
                                        >
                                            {perms.every(p => selectedPermissions.includes(p.key)) ? 'Unselect' : 'Select'} Category
                                        </button>
                                    </div>
                                    <div className="permission-grid">
                                        {perms.map(perm => (
                                            <div
                                                key={perm.key}
                                                className={`permission-pill ${selectedPermissions.includes(perm.key) ? 'active' : ''}`}
                                                onClick={() => handlePermissionToggle(perm.key)}
                                            >
                                                <span className="p-icon">{perm.icon}</span>
                                                <span className="p-label">{perm.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Direct login section removed as requested */}

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={isLoading}>
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditAdminModal;
